import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@vnetwork/ui/primitives';
import { brand as brandConfig } from '@/config/brand';
import styles from './AuthShell.module.css';

// Copied 1:1 from @vnetwork/ui src/templates/_shells/AuthShell.tsx — templates
// are reference compositions the package does not publish, so they are mirrored
// into the app. Only the Input import path and the default logoSrc differ.

export interface AuthShellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Small label above the title (e.g. "— business"). */
  eyebrow?: ReactNode;
  /** Banner rendered above the card (e.g. an info notice). */
  notice?: ReactNode;
  /** The form / body. */
  children: ReactNode;
  /** Bottom helper line (e.g. "Don't have an account? Sign up"). */
  footer?: ReactNode;
  /** Optional brand panel — when set, renders a split-screen layout. */
  brand?: ReactNode;
  /** Optional promo panel shown beside the card in the centered layout (e.g. a
   *  campaign banner on the right of the sign-up form). Takes precedence over `brand`. */
  aside?: ReactNode;
  /** Optional pane forming the other half of a single split block (form | pane).
   *  Takes precedence over `aside` and `brand`. */
  splitPane?: ReactNode;
  /** Logo image URL shown above the title. */
  logoSrc?: string;
}

/**
 * Centered authentication layout (sign in / sign up). Pass `brand` to switch
 * to a split-screen layout with a brand panel on the left.
 */
export function AuthShell({
  title,
  subtitle,
  eyebrow,
  notice,
  children,
  footer,
  brand,
  aside,
  splitPane,
  logoSrc = brandConfig.assets.icon,
}: AuthShellProps) {
  const card = (
    <div className={styles.cardWrap}>
      <img className={styles.logo} src={logoSrc} alt={brandConfig.shortName} width={44} height={44} />
      {notice ? <div className={styles.notice}>{notice}</div> : null}
      <div className={styles.card}>
        {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
        <h1 className={styles.title}>{title}</h1>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        <div className={styles.body}>{children}</div>
      </div>
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );

  // Single split block: form on one half, promo pane on the other. Logo + footer sit
  // INSIDE the form half. Stacks on mobile.
  if (splitPane) {
    return (
      <div className={styles.centered}>
        <div className={styles.splitCard}>
          <div className={styles.splitForm}>
            <img
              className={styles.splitLogo}
              src={logoSrc}
              alt={brandConfig.shortName}
              width={40}
              height={40}
            />
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            <h1 className={styles.title}>{title}</h1>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            {notice ? <div className={styles.notice}>{notice}</div> : null}
            <div className={styles.body}>{children}</div>
            {footer ? <div className={styles.splitFooter}>{footer}</div> : null}
          </div>
          <div className={styles.splitAside}>{splitPane}</div>
        </div>
      </div>
    );
  }

  // Centered card with a promo panel to its right (hidden on narrow viewports).
  if (aside) {
    return (
      <div className={styles.centered}>
        <div className={styles.pair}>
          {card}
          <aside className={styles.aside}>{aside}</aside>
        </div>
      </div>
    );
  }

  if (brand) {
    return (
      <div className={styles.split}>
        <div className={styles.brand}>{brand}</div>
        <div className={styles.formPane}>{card}</div>
      </div>
    );
  }

  return <div className={styles.centered}>{card}</div>;
}

/**
 * Password field with a show/hide toggle — reused by sign in / sign up.
 * (forwardRef is the only change from the template's version, so react-hook-form
 * can register the underlying input.)
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & { invalid?: boolean }
>(function PasswordInput(props, ref) {
  const [show, setShow] = useState(false);
  return (
    <Input
      ref={ref}
      type={show ? 'text' : 'password'}
      endSlot={
        <button
          type="button"
          className={styles.reveal}
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((s) => !s)}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      }
      {...props}
    />
  );
});
